import { useState, useEffect } from 'react';
import { CheckCircle, XCircle, Sparkles } from 'lucide-react';
import { Loader } from './ui/Loader';
import { API_PUBLIC_BASE } from '../utils/supabase/info';
import { supabase } from '../utils/supabase/client';
import { devLog, devError } from '../utils/logger';

interface EmailVerificationPageProps {
  onVerified: (isEmailChange: boolean) => void; // ✅ Pass isEmailChange flag
  onError: () => void;
}

export function EmailVerificationPage({ onVerified, onError }: EmailVerificationPageProps) {
  const [status, setStatus] = useState<'verifying' | 'success' | 'error'>('verifying');
  const [errorMessage, setErrorMessage] = useState('');
  const [userType, setUserType] = useState<'talent' | 'employer' | null>(null);
  const [userId, setUserId] = useState<string | null>(null);
  const [isEmailChange, setIsEmailChange] = useState(false); // Track if this was an email change
  const [isUserLoggedIn, setIsUserLoggedIn] = useState(false); // Track if user is currently logged in

  useEffect(() => {
    verifyEmail();
  }, []);

  const verifyEmail = async () => {
    try {
      // Extract token from URL hash
      const hash = window.location.hash;
      const tokenMatch = hash.match(/token=([^&]+)/);
      
      if (!tokenMatch) {
        setStatus('error');
        setErrorMessage('Ugyldig verifiseringslenke. Mangler token.');
        return;
      }

      const token = tokenMatch[1];

      // ✅ Check if user is currently logged in
      const { data: { session } } = await supabase.auth.getSession();
      setIsUserLoggedIn(!!session);

      // Call backend to verify token
      const response = await fetch(
        `${API_PUBLIC_BASE}/verify-email-token`,
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({ token }),
        }
      );

      const data = await response.json();

      if (!response.ok) {
        setStatus('error');
        setErrorMessage(data.error || 'Kunne ikke bekrefte e-posten. Prøv igjen senere.');
        return;
      }

      // Success! Store user info
      setUserId(data.userId);
      setUserType(data.userType);
      setStatus('success');

      // ✅ Check if this was an email change (not signup)
      const emailChanged = data.emailChanged === true;
      setIsEmailChange(emailChanged);

      // ✅ Clear pending verification from localStorage (if it exists)
      localStorage.removeItem('pendingVerificationEmail');

      // Set flag to show welcome screen after navigation (SIGNUP ONLY)
      if (!emailChanged) {
        localStorage.setItem(`isNewlyVerifiedUser_${data.userId}`, 'true');
      }
      
      devLog('✅ Email verified successfully for user:', data.userId, 'Type:', data.userType, 'Email change:', emailChanged, 'Logged in:', !!session);
    } catch (err) {
      devError('Email verification error:', err);
      setStatus('error');
      setErrorMessage('Noe gikk galt. Prøv igjen senere.');
    }
  };

  const handleContinue = () => {
    // Clear the hash to prevent re-triggering verification mode
    window.location.hash = '';
    onVerified(isEmailChange); // ✅ Pass isEmailChange flag
  };

  const handleGoBack = () => {
    window.location.hash = '';
    onError();
  };

  return (
    <div className="fixed inset-0 bg-gradient-to-br from-purple-100 via-pink-100 to-orange-100 flex items-center justify-center p-4 pb-safe z-[100] overflow-y-auto">
      <div className="bg-white rounded-3xl shadow-2xl border-2 border-purple-200 max-w-lg w-full p-4 sm:p-6 md:p-8 lg:p-10 relative my-auto max-h-[90vh] overflow-y-auto">
        
        {/* Verifying state */}
        {status === 'verifying' && (
          <div className="text-center">
            {/* Animated loader */}
            <div className="w-24 h-24 bg-gradient-to-br from-purple-100 to-pink-100 rounded-full flex items-center justify-center mx-auto mb-6">
              <Loader size="2xl" className="text-purple-600" />
            </div>

            <h2 className="text-2xl sm:text-3xl text-gray-900 mb-3">
              Bekrefter e-posten din...
            </h2>

            <p className="text-gray-600">
              Vennligst vent mens vi bekrefter kontoen din.
            </p>
          </div>
        )}

        {/* Success state */}
        {status === 'success' && (
          <div className="text-center">
            {/* Success icon with gradient background */}
            <div className="w-24 h-24 bg-gradient-to-br from-green-100 to-emerald-100 rounded-full flex items-center justify-center mx-auto mb-6 animate-in zoom-in duration-300">
              <CheckCircle className="w-12 h-12 text-green-600" />
            </div>

            <h2 className="text-2xl sm:text-3xl bg-gradient-to-r from-green-600 to-emerald-600 bg-clip-text text-transparent pb-1 mb-4">
              {isEmailChange ? 'E-postadresse oppdatert!' : 'E-post bekreftet!'}
            </h2>

            <p className="text-gray-700 text-lg mb-2">
              {isEmailChange 
                ? 'Din e-postadresse er nå oppdatert.' 
                : 'Din e-postadresse er nå verifisert.'}
            </p>

            {!isEmailChange && (
              <div className="bg-gradient-to-br from-purple-50 to-pink-50 rounded-2xl p-6 mb-8 border border-purple-100">
                <div className="flex items-center justify-center gap-2 mb-2">
                  <Sparkles className="w-5 h-5 text-purple-600" />
                  <p className="text-purple-900 font-semibold">
                    Velkommen til Talentfunn!
                  </p>
                </div>
                <p className="text-sm text-gray-700">
                  {userType === 'employer' 
                    ? 'Du kan nå begynne å utforske plattformen. Merk at du må vente på godkjenning fra admin før du får full tilgang.'
                    : 'Du kan nå begynne å bygge din profil og vise frem talentene dine!'}
                </p>
              </div>
            )}

            {/* Email change specific message */}
            {isEmailChange && (
              <div className="bg-green-50 border border-green-200 rounded-xl p-4 mb-6">
                <p className="text-sm text-green-800">
                  {isUserLoggedIn 
                    ? 'Du er fortsatt innlogget og kan nå bruke din nye e-postadresse.'
                    : 'Du kan nå logge inn med din nye e-postadresse.'}
                </p>
              </div>
            )}

            {/* Continue button */}
            <button
              onClick={handleContinue}
              className="w-full py-4 px-6 bg-gradient-to-r from-purple-600 to-pink-600 text-white rounded-xl hover:shadow-lg transition-all font-semibold text-lg"
            >
              Gå til Talentfunn
            </button>
          </div>
        )}

        {/* Error state */}
        {status === 'error' && (
          <div className="text-center">
            {/* Error icon */}
            <div className="w-24 h-24 bg-gradient-to-br from-red-100 to-pink-100 rounded-full flex items-center justify-center mx-auto mb-6">
              <XCircle className="w-12 h-12 text-red-600" />
            </div>

            <h2 className="text-2xl sm:text-3xl text-gray-900 mb-4">
              Kunne ikke bekrefte e-post
            </h2>

            <div className="bg-red-50 border-2 border-red-200 rounded-xl p-4 mb-6">
              <p className="text-red-700">{errorMessage}</p>
            </div>

            <p className="text-gray-600 mb-8">
              Lenken kan ha utløpt eller allerede blitt brukt. Prøv å logge inn, eller be om en ny bekreftelseslenke.
            </p>

            {/* Go back button */}
            <button
              onClick={handleGoBack}
              className="w-full py-4 px-6 bg-gradient-to-r from-purple-600 to-pink-600 text-white rounded-xl hover:shadow-lg transition-all font-semibold text-lg"
            >
              Tilbake til innlogging
            </button>
          </div>
        )}
      </div>
    </div>
  );
}
